import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  TextField
} from '@deque/cauldron-react';
import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';

import queryString from 'query-string';
import getProductFromUrl from '~/app/utils/get-product-from-url';
import { analyticsEvent, createAccount } from '../api-client';
import dequeLogo from '../assets/images/deque.png';
import {
  HubSpotProperties,
  originalRedirectQueryParam,
  pendingPostAuthRedirectStorageKey,
  postAuthRedirectQueryParam
} from '../constants';
import { useCrossProductAnalytics } from '../contexts/analytics';
import { useAuthContext } from '../contexts/auth';
import { useFeatureFlagState } from '../contexts/featureFlags';
import { useOnPremValue } from '../contexts/onPrem';
import billingClient from '../utils/billing-client/client-v2';
import setAuthComplete from '../utils/set-auth-complete';
import { isValidRedirectURI } from '../utils/validate-redirect';
import validate, { type Errors } from '../utils/validate-user-form';
import styles from './FirstLoginAlert.css';
import ScrimmedLoader from './ScrimmedLoader';
import TermsAndConditionsCheckbox from './TermsAndConditionsCheckbox';

type FirstLoginAlertProps = {
  initialShowAlert?: boolean;
};

const FirstLoginAlert = ({
  initialShowAlert = false
}: FirstLoginAlertProps): ReactElement | null => {
  const { t } = useTranslation();
  const {
    user,
    billingUser,
    checkForUser,
    loading: authLoading
  } = useAuthContext();
  const loadWrap = useRef<HTMLDivElement>(null);
  const agree = useRef<HTMLInputElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const firstName = useRef<HTMLInputElement>(null);
  const lastName = useRef<HTMLInputElement>(null);
  const company = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [show, setShow] = useState(initialShowAlert);
  const [loading, setLoading] = useState(false);
  const [fromExtension, setFromExtension] = useState(false);
  const isOnPrem = useOnPremValue();
  const productSlug = getProductFromUrl(window.location.pathname);
  const analytics = useCrossProductAnalytics(productSlug);
  const queryParamsObj = queryString.parse(window.location.search, {
    // This is the default, but we're being explicit here.
    decode: true
  });
  const isSupportRegistrationAllowed = useFeatureFlagState(
    'support_registration'
  );

  const onClose = () => {
    setShow(false);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    /* istanbul ignore if */
    if (
      !firstName.current ||
      !lastName.current ||
      !email.current ||
      !company.current ||
      !agree.current ||
      !user ||
      !user.token
    ) {
      setErrors({
        alert: t('Something went wrong. Please try again.')
      });
      return;
    }

    const userErrors = validate(
      {
        firstName: firstName.current,
        lastName: lastName.current,
        email: email.current,
        company: company.current,
        agree: agree.current
      },
      // We don't validate the password here, so we don't need password requirements.
      {},
      t
    );

    setErrors(userErrors);

    if (Object.keys(userErrors).length) {
      return;
    }

    setLoading(true);

    try {
      // check to see if there is already a billing user, if so, do not attempt to create one
      if (!billingUser) {
        await billingClient.users.create(
          {
            email: email.current.value,
            name: `${firstName.current.value} ${lastName.current.value}`
          },
          user.token
        );
      }

      await createAccount(
        user.id,
        {
          firstName: firstName.current.value,
          lastName: lastName.current.value,
          company: company.current.value
        },
        user.token
      );

      // checkForUser updates v2 billing user
      await checkForUser();

      TagManager.dataLayer({
        dataLayer: {
          event: 'axeFinalization'
        }
      });

      await Promise.all([
        analyticsEvent(user.token, HubSpotProperties.AXE_FIRST_LOGIN_DATE),
        analyticsEvent(user.token, HubSpotProperties.HS_MARKETABLE_STATUS, true)
      ]);
      analytics.acceptTerms();
      onClose();
      setAuthComplete();

      // The pending redirect is single-use: clear it on any successful
      // finalization, not just the ones that carry the query param.
      sessionStorage.removeItem(pendingPostAuthRedirectStorageKey);

      const postAuthRedirect = queryParamsObj[postAuthRedirectQueryParam];
      if (
        typeof postAuthRedirect === 'string' &&
        isValidRedirectURI(postAuthRedirect)
      ) {
        window.location.assign(postAuthRedirect);
      }
    } catch {
      setErrors({
        alert: t('Something went wrong. Please try again.')
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const shouldShow =
      !isOnPrem && user && !show && !user.hasAccountEntry && !authLoading;

    if (user && user.hasAccountEntry) {
      const originalRedirectURI = queryParamsObj[originalRedirectQueryParam];
      // Redirect to support site if redirection query exists
      if (
        originalRedirectURI &&
        typeof originalRedirectURI === 'string' &&
        isValidRedirectURI(originalRedirectURI) &&
        isSupportRegistrationAllowed
      ) {
        window.location.assign(originalRedirectURI);
      }
    }
    if (!shouldShow) {
      return;
    }

    setShow(true);
  }, [user, isSupportRegistrationAllowed]);

  useEffect(() => {
    if (!loading) {
      return;
    }
    /* istanbul ignore next */
    loadWrap.current?.focus();
  }, [loading]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('fromextension')) {
      setFromExtension(true);
    }
  }, []);

  return user ? (
    <Alert
      show={show}
      className={styles.alert}
      onClose={onClose}
      heading={
        <img className={styles.headingImage} src={dequeLogo} alt={t('Deque')} />
      }
    >
      <form noValidate onSubmit={onSubmit}>
        <AlertContent>
          {loading && (
            <ScrimmedLoader
              label={t('Finalizing your account...')}
              loaderRef={loadWrap}
            />
          )}
          {errors.alert && (
            <div className="Error" role="alert">
              {errors.alert}
            </div>
          )}
          <p>
            {t(
              "We'll use the following information to finalize your account. Please provide the missing information."
            )}
            {fromExtension && (
              <>
                <br />
                <strong>
                  {t(
                    'You will be sent back to the Axe extension after creating your account.'
                  )}
                </strong>
              </>
            )}
          </p>
          <TextField
            label={t('First name')}
            defaultValue={user.firstName}
            disabled={!!user.firstName}
            required={!user.firstName}
            error={errors.firstName}
            fieldRef={firstName}
          />
          <TextField
            label={t('Last name')}
            defaultValue={user.lastName}
            disabled={!!user.lastName}
            required={!user.lastName}
            error={errors.lastName}
            fieldRef={lastName}
          />
          <TextField
            label={t('Business email')}
            defaultValue={user.email}
            disabled={!!user.email}
            required={!user.email}
            error={errors.email}
            fieldRef={email}
          />
          <TextField
            label={t('Company')}
            defaultValue={user.company}
            disabled={!!user.company}
            required={!user.company}
            error={errors.company}
            fieldRef={company}
          />
          <TermsAndConditionsCheckbox
            id="terms"
            name="terms"
            checkboxRef={agree}
            invalid={!!errors.agree}
            className={styles.agree}
            error={errors.agree}
          />
        </AlertContent>
        <AlertActions>
          <Button type="submit">{t('Create')}</Button>
        </AlertActions>
      </form>
    </Alert>
  ) : null;
};

export default FirstLoginAlert;
